import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import type { EntityKind, RmsSchema } from "../Dashboard/types";
import type { RmsView } from "./views";
import type { LegacyViewGroup, RmsStrip } from "./stripModel";
import type { RmsDashboardConfig } from "./dashboardConfig";

// Response shape of GET /api/v2/rms/bootstrap. Snake_case matches the backend.
export interface RmsBootstrap {
  rms_components?: boolean;
  // Whether this viewer reads the organization's record page layouts: the Layouts tab in page
  // mode. Every member does (#13314). Absent from an older backend, read as no.
  rms_page_layouts?: boolean;
  // Whether this viewer arranges them: the Layouts editor's Edit, the record page's "Edit
  // layout", the view summary's shortcut. The organization's RMS managers and platform admins
  // (#13314). Absent from an older backend, read as no.
  rms_page_layouts_edit?: boolean;
  rms_component_authoring?: boolean;
  // Whether the viewer may change the organization-wide RMS settings: the display config and the
  // record model (#13080). True for an organization admin or manager and a platform admin. Absent
  // from an older backend, read as no.
  rms_manager?: boolean;
  views: RmsView[];
  // The user's strip: order, pin state and groups (Data/strip.ts). Absent
  // from a backend older than the strip layouts, which sent `view_groups` and
  // per-view pin and group fields instead (`stripOf` reads either).
  strip?: RmsStrip;
  view_groups?: LegacyViewGroup[];
  dashboard_config: RmsDashboardConfig;
  schemas: Partial<Record<EntityKind, RmsSchema>>;
  // Org flag gating review-related activity columns (Needs Review,
  // Approved By). False by default — safe when the backend is older
  // than this field.
  rms_review_enabled: boolean;
  // Whether the org has the Finpilot -> Bipsync push enabled. Gates the
  // Activity "Review queue" toggle (a Bipsync-push-only surface). Optional so
  // an older backend that omits the field reads as undefined -> falsy ->
  // toggle hidden (fail-closed).
  bipsync_push_enabled?: boolean;
  // Org flag arming scheduled AI autofill cadences (#10874). Optional so an
  // older backend that omits the field reads as undefined -> falsy -> the
  // cadence control stays locked to "Never" (fail-closed).
  rms_autofill_schedules?: boolean;
  // Grid of fact names for the vehicle Terms tab, or absent for the orgs that
  // don't have that tab — which is nearly all of them. The backend resolves
  // which org gets one, so nothing here identifies the client (#10972).
  // Fixed-position: `""` is a deliberate blank cell. Absent (or empty) means
  // no Terms tab at all, which is also the fail-closed reading of an older
  // backend that omits the field.
  terms_grid_layout?: string[][] | null;
  // The organization's colours (#13752, P3.5), in order: the charts' default order when set.
  // Absent from an older backend, read as none.
  palette?: PaletteColor[];
  // What a click on an interaction does (#14870): its own page, or the side viewer beside the
  // record. The organization's choice in Appearance; absent from an older backend, read as the
  // page.
  interaction_opens?: InteractionOpens;
  // The holdings number columns whose total is on (#14991): the organization's choice in
  // Appearance, resolved by the server (its "Market value" columns when it made none).
  // Percentage columns are always totalled and are not listed. Absent from an older backend.
  holdings_totalled_columns?: number[];
}

/** Where an interaction opens from a record's lists (#14870): its own page, or the side viewer. */
export type InteractionOpens = "page" | "preview";

/** A colour of the organization's palette (#13752, P3): its name and its `#rrggbb`. */
export interface PaletteColor {
  name: string;
  hex: string;
}

/** Every organization's bootstrap; invalidating or cancelling it reaches all of them. */
export const RMS_BOOTSTRAP_QUERY_KEY = ["rms", "bootstrap"] as const;

/**
 * One organization's bootstrap, the one entry every reader shares. It used to be two entries
 * (the views and schemas under the bare key, the component capability under the organization),
 * which fetched `/api/v2/rms/bootstrap` twice on every RMS load (#11849): one response serves
 * both, and keying it by organization keeps one organization's answer from another's surfaces.
 */
export const rmsBootstrapQueryKey = (orgId: string | undefined) =>
  [...RMS_BOOTSTRAP_QUERY_KEY, orgId] as const;

const bootstrapUrl = `${BASE_API_URL_V2}/api/v2/rms/bootstrap`;

const fetchRmsBootstrap = async (): Promise<RmsBootstrap> => {
  const response = await fpFetch(bootstrapUrl, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch RMS bootstrap: ${response.status}`);
  }
  return response.json();
};

// Shared RMS bootstrap query. Every downstream hook (views, view groups,
// dashboard config, entity schemas, the component capability) points at this
// same queryKey, so TanStack Query dedupes them into a single network request. Consumers derive their
// slice with `select` — memoized per-observer, so components only re-render
// when their slice changes.
//
// Read once per session (spec rms-bootstrap-once-per-session). Every RMS page
// needs this data, and it is the same on every page, so moving between pages
// never reads it again: it never goes stale by time, so a page that mounts a
// new reader (a record page's schema, views and capability readers) is served
// from the cache however long the user has been working, and it is never
// collected while the tab is open, so leaving RMS for a while and coming back
// does not read it either. Three things read it again, each in the
// background (the page keeps what it shows until the answer lands; no reader
// waits on a background read):
//  - a write the app makes to what it holds invalidates it, or updates it in
//    place (views, the strip, schemas, the dashboard config); an invalidated
//    entry is stale, so its next reader re-reads it;
//  - coming back to the tab once the data is BOOTSTRAP_FOCUS_REFRESH_MS old,
//    to pick up what teammates changed;
//  - a page mounting a reader once the data is BOOTSTRAP_MOUNT_REFRESH_MS old,
//    for a tab kept in view all day, which never regains focus, and for two
//    tabs of one user, which would otherwise drift apart for good.
// Functions of the data's age rather than a bounded staleTime, because a
// bounded staleTime would make every page mount after the window re-read it.
/** How old the bootstrap must be before coming back to the tab re-reads it. */
export const BOOTSTRAP_FOCUS_REFRESH_MS = 5 * 60 * 1000;
/** How old the bootstrap must be before a page that mounts a reader re-reads it. */
export const BOOTSTRAP_MOUNT_REFRESH_MS = 30 * 60 * 1000;

interface BootstrapQueryState {
  state: { dataUpdatedAt: number; isInvalidated: boolean };
}

// "always" past the window; true for an invalidated entry, which react-query then re-reads as
// any stale one; false otherwise, which reads nothing.
const refreshWhenOlderThan =
  (windowMs: number) =>
  (query: BootstrapQueryState): boolean | "always" =>
    query.state.isInvalidated ||
    (Date.now() - query.state.dataUpdatedAt >= windowMs ? "always" : false);

/** The bootstrap read as a query: the hook below, and the Home prefetch's one-off read. */
export const rmsBootstrapQuery = (orgId: string | undefined) => ({
  queryKey: rmsBootstrapQueryKey(orgId),
  queryFn: fetchRmsBootstrap,
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnWindowFocus: refreshWhenOlderThan(BOOTSTRAP_FOCUS_REFRESH_MS),
  refetchOnMount: refreshWhenOlderThan(BOOTSTRAP_MOUNT_REFRESH_MS),
});

export function useRmsBootstrap<TData = RmsBootstrap>(options?: {
  select?: (data: RmsBootstrap) => TData;
  /** False on a surface RMS does not serve (the claims queue), whose
   * deployment may not register the RMS routes at all (#11422). */
  enabled?: boolean;
}) {
  const orgId = useOrgId();
  return useQuery<RmsBootstrap, Error, TData>({
    ...rmsBootstrapQuery(orgId),
    enabled: Boolean(orgId) && (options?.enabled ?? true),
    select: options?.select,
  });
}

// Optimistic-update helpers for the bootstrap cache. Callers that previously
// poked at per-resource query keys (views list, view-groups list) should use
// these so the edit lands on the single shared cache entry.
//
// Every optimistic write first cancels any bootstrap fetch already in the
// air. Without this, a background refetch (window-focus fires exactly when
// the user comes back to interact) can resolve AFTER the optimistic write
// and clobber it with the server's stale answer — the view strip's pin
// reorder visibly flashed the old order this way (#11426). Fire-and-forget:
// cancellation only needs to be REQUESTED before the write, and the mutation
// that follows re-invalidates on success either way.
const cancelInFlightBootstrap = (queryClient: QueryClient): void => {
  void queryClient.cancelQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });
};

//
// A write reaches the organization the viewer is in and no other: staff move between
// organizations without a reload, and another organization's cached bootstrap must never show
// a view or group created here.
export const setBootstrapViews = (
  queryClient: QueryClient,
  orgId: string | undefined,
  updater: (views: RmsView[]) => RmsView[]
): void => {
  cancelInFlightBootstrap(queryClient);
  // An entry without a views list (a bootstrap read only for its capabilities) is left alone: a
  // write that the server accepted must not fail because a cache it updates has nothing to update.
  queryClient.setQueryData<RmsBootstrap>(rmsBootstrapQueryKey(orgId), (old) =>
    old?.views ? { ...old, views: updater(old.views) } : old
  );
};

// The line a control withheld from a non-manager shows, where the controls are (#13080).
export const RMS_MANAGER_ONLY_MESSAGE =
  "Only organization admins and managers can change this.";

/**
 * Whether the viewer may change the organization-wide RMS settings (#13080): `true` or `false`
 * once the bootstrap has answered (a bootstrap without the flag answers `false`), `undefined`
 * before it has. Callers withhold only on `false`: the server refuses the write either way, and
 * an unanswered bootstrap is not a reason to show a restriction.
 */
export const useIsRmsManager = (): boolean | undefined =>
  useRmsBootstrap<boolean>({ select: (data) => data.rms_manager === true })
    .data;

// The vehicle Terms grid, or null when this org has no Terms tab. Null is the
// single answer both the tab strip and the panel router read, so neither can
// drift into offering a tab the other won't render. An empty layout counts as
// unconfigured, so clearing it turns the tab off rather than rendering a
// zero-row grid.
export const useTermsGridLayout = () =>
  useRmsBootstrap<string[][] | null>({
    select: (data) =>
      data.terms_grid_layout && data.terms_grid_layout.length > 0
        ? data.terms_grid_layout
        : null,
  });

const NO_PALETTE: readonly string[] = [];
const paletteHexes = (data: RmsBootstrap): readonly string[] =>
  data.palette && data.palette.length > 0
    ? data.palette.map((color) => color.hex)
    : NO_PALETTE;

/**
 * The organization's palette as hexes, in order: the default order of a chart's uncoloured series
 * and slices (#13752, P3.4). Empty while the bootstrap has not answered, and for an organization
 * without one, so the house palette stays the default.
 */
export const useOrganizationPalette = (): readonly string[] =>
  useRmsBootstrap<readonly string[]>({ select: paletteHexes }).data ??
  NO_PALETTE;

const NO_TOTALLED_COLUMNS: readonly number[] = [];
const holdingsTotalledColumnsOf = (data: RmsBootstrap): readonly number[] =>
  data.holdings_totalled_columns ?? NO_TOTALLED_COLUMNS;

/**
 * The holdings number columns whose total is on in this organization (#14991), by column id:
 * the totals row under the record's Holdings tab sums these and every percentage column.
 * Undefined until the bootstrap answers.
 */
export const useHoldingsTotalledColumns = (): readonly number[] | undefined =>
  useRmsBootstrap<readonly number[]>({ select: holdingsTotalledColumnsOf })
    .data;

const interactionOpensOf = (data: RmsBootstrap): InteractionOpens =>
  data.interaction_opens === "preview" ? "preview" : "page";

/**
 * What a click on an interaction does in this organization (#14870): `"preview"` opens it in the
 * side viewer beside the record, `"page"` opens its own page.
 *
 * It reads the bootstrap from the cache only and never asks for it: the shared grid that reads it
 * also renders on pages RMS does not serve (the claims intake), which must send no RMS request.
 * Every RMS surface has loaded the bootstrap before its grid renders; without one cached, the
 * answer is the full page.
 */
export const useInteractionOpens = (): InteractionOpens => {
  const orgId = useOrgId();
  return (
    useQuery<RmsBootstrap, Error, InteractionOpens>({
      ...rmsBootstrapQuery(orgId),
      enabled: false,
      select: interactionOpensOf,
    }).data ?? "page"
  );
};
